import React, { FC, memo, useRef } from "react"
import { Checkbox, Input } from "antd"
import { useApolloClient } from "@apollo/client"
import FormItem from "antd/lib/form/FormItem"
import { useTranslation } from "react-i18next"
import { NewBookingInput } from "../../../../../generated/graphql"
import { NewBookingInputKeys } from "../../../BookingSummary/BookingSummary"
import LayoutForm from "../../../../../views/LayoutForm/LayoutForm"
import { useMediaSize } from "../../../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { GET_BILLING_ADDRESS_SUGGESTION } from "../billingAddressSuggestion.graphql"

type BillingAddressInputProps = {
    bookingData?: NewBookingInput
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    showAlert?: boolean
}

type TextKey = "billingCompanyName" | "billingFirstname" | "billingLastname" | "billingAddition" | "billingStreet" | "billingPostCode" | "billingCity"

/**
 * Abweichende Rechnungsadresse nur für diese Buchung (Schalter „Abweichende Rechnungsadresse im Checkout“ im Web Modul).
 * Gespeichert wird sie nur an der Buchung. Beim Setzen des Hakens mit leeren Feldern füllt eine eigene Query vor
 * (letzte Buchung des Teilnehmers, sonst Teilnehmer, sonst Bucher). Nicht über die Bucher-Query: der geladene Bucher
 * geht teils als Input an updateBookerAccountEndUserApp zurück, dessen Input keine Rechnungsfelder kennt.
 */
const BillingAddressInput: FC<BillingAddressInputProps> = ({ bookingData, updateBookingData, showAlert }) => {
    const { t } = useTranslation()
    const { media } = useMediaSize()
    const active = bookingData?.hasBillingAddress === true
    const apolloClient = useApolloClient()
    // Stand nach der Antwort: bis dahin kann getippt, der Haken entfernt oder der Teilnehmer gewechselt sein.
    const latest = useRef(bookingData)
    latest.current = bookingData

    const toggle = async (checked: boolean) => {
        updateBookingData("hasBillingAddress", checked)

        const attendeeId = bookingData?.attendeeId
        const empty = TEXT_FIELDS.every((field) => !(bookingData?.[field.key] ?? "").trim())

        if (!checked || !attendeeId || !empty) return
        try {
            const { data } = await apolloClient.query({ query: GET_BILLING_ADDRESS_SUGGESTION, variables: { attendeeId }, fetchPolicy: "network-only" })
            const suggestion = data?.billingAddressSuggestionEndUserApp
            const now = latest.current

            if (!suggestion || now?.hasBillingAddress !== true || now.attendeeId !== attendeeId) return
            // Nur leere Felder füllen: was inzwischen getippt wurde, bleibt.
            TEXT_FIELDS.forEach((field) => suggestion[field.key] && !(now[field.key] ?? "").trim() && updateBookingData(field.key, suggestion[field.key]))
        } catch {
            // Nur Komfort: ohne Vorschlag (z. B. älteres Backend) bleibt das Formular leer.
        }
    }

    const missing = (key: TextKey) => {
        if (!showAlert || !active) return false
        const value = (bookingData?.[key] ?? "").trim()

        if (key === "billingCompanyName" || key === "billingLastname") {
            return !(bookingData?.billingCompanyName ?? "").trim() && !(bookingData?.billingLastname ?? "").trim()
        }

        return value.length === 0 && (key === "billingStreet" || key === "billingPostCode" || key === "billingCity")
    }

    return (
        <>
            <Checkbox checked={active} onChange={(event) => toggle(event.target.checked)} data-testid='billing-address-checkbox'>
                {t("bookingStepSummary.billingAddress.checkbox", "Abweichende Rechnungsadresse für diese Buchung")}
            </Checkbox>
            {active && (
                <LayoutForm columns={media === "xs" ? 1 : 2}>
                    {TEXT_FIELDS.map((field) => (
                        <FormItem
                            key={field.key}
                            label={t(`bookingStepSummary.billingAddress.${field.key}`, field.label)}
                            required={field.required}
                            validateStatus={missing(field.key) ? "error" : undefined}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                value={bookingData?.[field.key] ?? ""}
                                maxLength={200}
                                onChange={(event) => updateBookingData(field.key, event.target.value)}
                                data-testid={`billing-address-${field.key}`}
                            />
                        </FormItem>
                    ))}
                </LayoutForm>
            )}
        </>
    )
}

// Reihenfolge wie auf der Rechnung und im Admin-Dialog. Firma oder Nachname ist Pflicht, das sagt der Hinweis unten.
const TEXT_FIELDS: Array<{ key: TextKey; label: string; required?: boolean }> = [
    { key: "billingCompanyName", label: "Firma" },
    { key: "billingFirstname", label: "Vorname" },
    { key: "billingLastname", label: "Nachname" },
    { key: "billingAddition", label: "Adresszusatz (z. B. Abteilung)" },
    { key: "billingStreet", label: "Straße und Hausnummer", required: true },
    { key: "billingPostCode", label: "PLZ", required: true },
    { key: "billingCity", label: "Ort", required: true },
]

export default memo(BillingAddressInput)
